import { supabase } from "@/integrations/supabase/client";
import { AuthError } from "@supabase/supabase-js";
import { devError, devWarn } from "@/utils/secureLogger";

export const useSignIn = () => {
  const signIn = async (email: string, password: string, captchaToken?: string) => {
    try {
      // Add rate limiting check (using localStorage for demo)
      const attemptKey = `login_attempts_${email.toLowerCase()}`;
      const currentTime = new Date().getTime();
      const attemptData = localStorage.getItem(attemptKey);
      
      if (attemptData) {
        const { attempts, timestamp } = JSON.parse(attemptData);
        // Reset attempts if more than 15 minutes have passed
        if (currentTime - timestamp > 15 * 60 * 1000) {
          localStorage.setItem(attemptKey, JSON.stringify({ attempts: 1, timestamp: currentTime }));
        } 
        // Otherwise check if too many attempts
        else if (attempts >= 5) {
          devWarn("Too many login attempts");
          return {
            success: false, 
            error: { message: "Too many login attempts. Please try again later." }, 
            message: "Too many login attempts. Please try again later."
          };
        } else {
          // Increment attempts
          localStorage.setItem(attemptKey, JSON.stringify({ 
            attempts: attempts + 1, 
            timestamp
          }));
        }
      } else {
        // First attempt
        localStorage.setItem(attemptKey, JSON.stringify({ attempts: 1, timestamp: currentTime }));
      }
      
      // Proceed with sign in
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email: email.trim().toLowerCase(), 
        password,
        options: captchaToken ? { captchaToken } : undefined,
      });
      
      if (error) {
        devError("Error signing in:", error);
        return {
          success: false, 
          error, 
          message: error.message || "Invalid login credentials" 
        };
      }
      
      // Clear login attempts on successful login
      localStorage.removeItem(attemptKey);
      
      return { 
        success: true, 
        data, 
        error: null 
      };
    } catch (err) {
      devError("Error signing in:", err);
      const authError = err as AuthError;
      return { 
        success: false, 
        error: authError, 
        message: authError.message || "An unexpected error occurred" 
      };
    }
  };

  return { signIn };
};
